import React, { FC, ReactNode } from "react";
import Typography from "@mui/material/Typography";
import { DescriptionOutlined } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { indigo } from "@mui/material/colors";

const StyledNoteDiv = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  width: "600px",
  display: "flex",
  alignItems: "flex-start",
  textAlign: "left",
  marginTop: 10,
  marginBottom: 10,
  padding: theme.spacing(2.5, 3),
  backgroundColor: indigo[50],
  borderLeft: `5px solid ${theme.palette.primary.dark}`,
  borderRadius: 4,
}));

const StyledIcon = styled(DescriptionOutlined)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: 20,
  marginRight: 12,
  flexShrink: 0,
}));

const StyledHeading = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
}));

const StyledList = styled("ul")({
  margin: "8px 0 0",
  paddingLeft: 20,
});

const StyledListItem = styled("li")({
  marginBottom: 4,
  "&:last-of-type": {
    marginBottom: 0,
  },
});

const StyledDiv = styled("div")({
  width: "100%",
});

export interface PleaseNoteProps {
  /** A single note, or several notes rendered as bullet points */
  notes: ReactNode | ReactNode[];
  /** Lead-in sentence rendered above the notes */
  intro?: ReactNode;
  /** Heading shown above the note(s) */
  title?: string;
}

const PleaseNote: FC<PleaseNoteProps> = ({
  notes,
  intro,
  title = "Please Note",
}) => {
  const noteList = (Array.isArray(notes) ? notes : [notes]).filter(Boolean);

  if (!noteList.length) return null;

  return (
    <StyledNoteDiv>
      <StyledIcon />

      <StyledDiv>
        <StyledHeading>{title}</StyledHeading>

        {intro && (
          <Typography variant="body2" style={{ marginTop: 6 }}>
            {intro}
          </Typography>
        )}

        {intro || noteList.length > 1 ? (
          <StyledList>
            {noteList.map((note, i) => (
              <StyledListItem key={i}>
                <Typography variant="body2">{note}</Typography>
              </StyledListItem>
            ))}
          </StyledList>
        ) : (
          <Typography variant="body2" style={{ marginTop: 6 }}>
            {noteList[0]}
          </Typography>
        )}
      </StyledDiv>
    </StyledNoteDiv>
  );
};

export default PleaseNote;
